import React, { useState } from "react";

/**
 * Debug panel for Frame 001 Laboratory. Not for the presentation.
 * Not for users. For us — the researchers observing the material.
 *
 * Pressing H hides the entire panel so only the scene remains.
 */

const SECTIONS = [
  {
    id: "material",
    title: "Material",
    controls: [
      { key: "surfaceRoughness", min: 0, max: 1, step: 0.001 },
      { key: "subsurfaceScattering", min: 0, max: 5, step: 0.01 },
      { key: "translucency", min: 0, max: 1, step: 0.001 },
      { key: "opacity", min: 0, max: 1, step: 0.001 },
      { key: "internalDiffusion", min: 0, max: 1, step: 0.001 },
      { key: "surfaceDepth", min: 0, max: 4, step: 0.01 },
    ],
  },
  {
    id: "facets",
    title: "Facets",
    controls: [
      { key: "facetDensity", min: 2, max: 100, step: 1 },
      { key: "facetVisibility", min: 0, max: 1, step: 0.001 },
      { key: "facetTransparency", min: 0, max: 1, step: 0.001 },
      { key: "facetScale", min: 0.5, max: 8, step: 0.01 },
    ],
  },
  {
    id: "light",
    title: "Light",
    controls: [
      { key: "lightIntensity", min: 0, max: 40, step: 0.01 },
      { key: "lightRadius", min: 0.01, max: 1.5, step: 0.001 },
      { key: "lightColor", type: "color" },
      { key: "lightDistance", min: 0.2, max: 8, step: 0.01 },
      { key: "pulseAmplitude", min: 0, max: 1, step: 0.001 },
      { key: "pulseFrequency", min: 0, max: 1, step: 0.001 },
      { key: "refractionStrength", min: 1, max: 2.5, step: 0.001 },
    ],
  },
  {
    id: "camera",
    title: "Camera",
    controls: [
      { key: "cameraDistance", min: 0.1, max: 3, step: 0.001 },
      { key: "cameraDriftAmplitude", min: 0, max: 0.5, step: 0.001 },
      { key: "cameraDriftSpeed", min: 0, max: 0.5, step: 0.001 },
    ],
  },
];


export default function DebugPanel({
  params,
  onParamChange,
  onReset,
  onScreenshot,
  onSavePreset,
  onLoadPreset,
  onHideUI,
  presets,
  currentPresetName,
}) {
  const [openSections, setOpenSections] = useState({
    material: true,
    facets: true,
    light: true,
    camera: false,
  });
  const [presetInputName, setPresetInputName] = useState("");
  const [selectedPreset, setSelectedPreset] = useState("");

  const toggleSection = (id) =>
    setOpenSections((s) => ({ ...s, [id]: !s[id] }));

  return (
    <aside
      data-testid="frame001-debug-panel"
      style={{
        position: "fixed",
        top: 16,
        right: 16,
        bottom: 16,
        width: 320,
        overflowY: "auto",
        background: "rgba(10,10,16,0.78)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: 6,
        padding: "16px 14px 20px",
        color: "#d6d3d1",
        fontFamily: "ui-monospace, SFMono-Regular, monospace",
        fontSize: 11,
        lineHeight: 1.5,
        zIndex: 10,
      }}
    >
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 14,
          paddingBottom: 10,
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <span
          style={{
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "#a8a29e",
            fontSize: 10,
          }}
        >
          Frame 001 · Lab
        </span>
        <button
          onClick={onHideUI}
          data-testid="btn-hide-ui"
          style={{
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.15)",
            color: "#a8a29e",
            fontFamily: "inherit",
            fontSize: 10,
            padding: "2px 8px",
            borderRadius: 3,
            cursor: "pointer",
            letterSpacing: "0.08em",
          }}
          title="Hide UI (H)"
        >
          hide (H)
        </button>
      </header>

      {SECTIONS.map((section) => (
        <Section
          key={section.id}
          title={section.title}
          open={openSections[section.id]}
          onToggle={() => toggleSection(section.id)}
        >
          {section.controls.map((c) => (
            <Control
              key={c.key}
              controlKey={c.key}
              value={params[c.key]}
              min={c.min}
              max={c.max}
              step={c.step}
              type={c.type}
              onChange={(v) => onParamChange(c.key, v)}
            />
          ))}
        </Section>
      ))}

      <section style={{ marginTop: 18 }}>
        <SectionHeader title="Actions" />
        <div style={{ display: "grid", gap: 6, marginTop: 8 }}>
          <ActionButton onClick={onReset} testId="btn-reset">
            reset scene
          </ActionButton>
          <ActionButton
            onClick={() => onScreenshot(currentPresetName || "unsaved")}
            testId="btn-screenshot"
          >
            screenshot + metadata
          </ActionButton>
        </div>

        <div style={{ marginTop: 14 }}>
          <SectionHeader title="Presets" small />
          <div
            style={{
              display: "flex",
              gap: 4,
              alignItems: "center",
              marginTop: 6,
            }}
          >
            <input
              type="text"
              placeholder="preset name"
              value={presetInputName}
              onChange={(e) => setPresetInputName(e.target.value)}
              data-testid="input-preset-name"
              style={inputStyle}
            />
            <button
              onClick={() => {
                if (presetInputName.trim()) {
                  onSavePreset(presetInputName.trim());
                  setPresetInputName("");
                }
              }}
              data-testid="btn-save-preset"
              style={smallButtonStyle}
            >
              save
            </button>
          </div>
          <div
            style={{
              display: "flex",
              gap: 4,
              alignItems: "center",
              marginTop: 6,
            }}
          >
            <select
              value={selectedPreset}
              onChange={(e) => setSelectedPreset(e.target.value)}
              data-testid="select-preset"
              style={{ ...inputStyle, cursor: "pointer" }}
            >
              <option value="">— load preset —</option>
              {Object.keys(presets || {}).map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            <button
              onClick={() => {
                if (selectedPreset) onLoadPreset(selectedPreset);
              }}
              data-testid="btn-load-preset"
              style={smallButtonStyle}
            >
              load
            </button>
          </div>
        </div>
      </section>

      <footer
        style={{
          marginTop: 20,
          paddingTop: 10,
          borderTop: "1px solid rgba(255,255,255,0.06)",
          color: "#57534e",
          fontStyle: "italic",
          fontSize: 10,
          lineHeight: 1.5,
        }}
      >
        The laboratory succeeds if we can spend an hour changing only
        the material and discover behaviors we did not anticipate.
      </footer>
    </aside>
  );
}


function Section({ title, open, onToggle, children }) {
  return (
    <section style={{ marginBottom: 12 }}>
      <button
        onClick={onToggle}
        style={{
          background: "transparent",
          border: "none",
          color: "#a8a29e",
          fontFamily: "inherit",
          fontSize: 10,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          cursor: "pointer",
          padding: "6px 0",
          width: "100%",
          textAlign: "left",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>{title}</span>
        <span style={{ color: "#57534e" }}>{open ? "−" : "+"}</span>
      </button>
      {open && <div style={{ marginTop: 4 }}>{children}</div>}
    </section>
  );
}


function SectionHeader({ title, small = false }) {
  return (
    <div
      style={{
        color: "#a8a29e",
        fontSize: small ? 9 : 10,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        marginBottom: 4,
      }}
    >
      {title}
    </div>
  );
}


function Control({ controlKey, value, min, max, step, type, onChange }) {
  if (type === "color") {
    return (
      <div style={{ marginBottom: 6 }}>
        <div style={rowStyle}>
          <label style={labelStyle}>{controlKey}</label>
          <span style={valueStyle}>{value}</span>
        </div>
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`control-${controlKey}`}
          style={{
            width: "100%",
            height: 22,
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 3,
            background: "transparent",
            cursor: "pointer",
            padding: 0,
          }}
        />
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 6 }}>
      <div style={rowStyle}>
        <label style={labelStyle}>{controlKey}</label>
        <span style={valueStyle}>
          {typeof value === "number" ? value.toFixed(step < 1 ? 3 : 0) : value}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        data-testid={`control-${controlKey}`}
        style={{
          width: "100%",
          accentColor: "#e08a72",
          cursor: "pointer",
        }}
      />
    </div>
  );
}


function ActionButton({ children, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.12)",
        color: "#d6d3d1",
        fontFamily: "inherit",
        fontSize: 10,
        padding: "6px 10px",
        borderRadius: 3,
        cursor: "pointer",
        letterSpacing: "0.08em",
        textAlign: "left",
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.background = "rgba(255,255,255,0.08)")
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.background = "rgba(255,255,255,0.03)")
      }
    >
      {children}
    </button>
  );
}

const rowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  marginBottom: 2,
};

const labelStyle = {
  color: "#d6d3d1",
  fontSize: 10,
  letterSpacing: 0,
};

const valueStyle = {
  color: "#78716c",
  fontSize: 10,
  fontFamily: "ui-monospace, SFMono-Regular, monospace",
};

const inputStyle = {
  flex: 1,
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.1)",
  color: "#d6d3d1",
  fontFamily: "inherit",
  fontSize: 10,
  padding: "4px 6px",
  borderRadius: 3,
  outline: "none",
};

const smallButtonStyle = {
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.12)",
  color: "#d6d3d1",
  fontFamily: "inherit",
  fontSize: 10,
  padding: "4px 10px",
  borderRadius: 3,
  cursor: "pointer",
  letterSpacing: "0.08em",
};
